import {
  ChevronsUpDown,
  Columns,
  Copy,
  Eye,
  FileText,
  LayoutDashboard,
  LayoutGrid,
  LayoutList,
  LayoutPanelLeft,
  Newspaper,
  Pen,
  RotateCcw,
  Rows,
  Share2,
  Shrink,
  Star,
  Trash2,
} from "lucide-react";
import { AgentEditIcon } from "../atoms/AgentEditIcon";
import { AiSparkleIcon } from "../atoms/AiSparkleIcon";
import { defineMenu } from "./configRegistry.types";
import type { MenuConfig } from "./configRegistry.types";
import type { InsightSectionKind } from "../../services/dataStories/generateStory.types";
import { LAYOUT_PRESETS } from "../../services/storyCanvas/layoutPresets";
import type { LayoutPresetId } from "../../services/storyCanvas/layoutPresets";
import type { PlanLayout } from "../../services/dataStories/storiesApi.types";

const LAYOUT_PRESET_ICONS = {
  "one-column": Rows,
  "two-columns": Columns,
  "three-columns": LayoutGrid,
  compact: Shrink,
} as const;

/**
 * Named menu configs — single source of truth for dropdown item sets.
 * Same ActionMenu design everywhere; only the named item set differs.
 * Consumers import by name: `menus.aiSummaryEdit`.
 */
export const menus = {
  /** Agents catalog card actions, in the Figma-specified visual order. */
  agentCardActions: defineMenu([
    { id: "edit", label: "Edit", icon: AgentEditIcon },
    { id: "duplicate", label: "Duplicate", icon: Copy },
    { id: "share", label: "Share", icon: Share2 },
    { id: "addToFavourite", label: "Add to favourite", icon: Star },
    { id: "delete", label: "Delete", icon: Trash2 },
  ]),

  /** AI summary widget edit menu (Figma reference: Edit manually / Improve summary). */
  aiSummaryEdit: defineMenu([
    { id: "editManually", label: "Edit manually", icon: Pen },
    { id: "improveSummary", label: "Improve summary", icon: AiSparkleIcon },
  ]),

  /** Chart-companion AI summary edit menu — Delete PATCHes is_summary_visible false. */
  aiChartSummaryEdit: defineMenu([
    { id: "editManually", label: "Edit manually", icon: Pen },
    { id: "improveSummary", label: "Improve summary", icon: AiSparkleIcon },
    { id: "delete", label: "Delete", icon: Trash2 },
  ]),

  /** Chart-companion AI narrative edit menu — Delete PATCHes is_narrative_visible false. */
  aiChartNarrativeEdit: defineMenu([
    { id: "editManually", label: "Edit manually", icon: Pen },
    { id: "improveSummary", label: "Improve narrative", icon: AiSparkleIcon },
    { id: "delete", label: "Delete", icon: Trash2 },
  ]),

  /** Story plan-builder narrative edit menu (current StoryAiEditDropdown item set). */
  aiPlanEdit: defineMenu([
    { id: "editManually", label: "Edit manually", icon: Pen },
    { id: "regenerate", label: "Regenerate", icon: RotateCcw },
  ]),

  /** Chart header sparkle dropdown (Figma reference: Ask MagicBI + insert
   *  actions). Charts can be generated without summary/narrative — these
   *  items insert the missing AI sections. UI only for now; handlers land
   *  with each flow. */
  chartAiActions: defineMenu([
    { id: "askMagicBI", label: "Ask MagicBI", icon: AiSparkleIcon },
    { id: "insertSummary", label: "Insert AI summary" },
    { id: "insertNarrative", label: "Insert AI narrative" },
    { id: "insertInsights", label: "Insert AI insights" },
  ]),

  /** Chart header overflow menu — keeps destructive/edit actions out of the
   * primary toolbar while retaining the existing chart-level behaviour. */
  chartWidgetMore: defineMenu([
    { id: "edit", label: "Edit", icon: Pen },
    { id: "fitToContent", label: "Fit to content", icon: ChevronsUpDown },
    { id: "delete", label: "Delete", icon: Trash2, danger: true },
  ]),

  /** A chart tile's ⋮ menu: the actions above plus Duplicate and View data (the report widget keeps `chartWidgetMore`). */
  chartTileMore: defineMenu([
    { id: "edit", label: "Edit", icon: Pen },
    { id: "fitToContent", label: "Fit to content", icon: ChevronsUpDown },
    { id: "duplicate", label: "Duplicate", icon: Copy },
    { id: "viewData", label: "View data", icon: Eye },
    { id: "delete", label: "Delete", icon: Trash2, danger: true },
  ]),

  /** Tile overflow menu for a tile with no other tile-level actions. */
  tileFitToContent: defineMenu([
    { id: "fitToContent", label: "Fit to content", icon: ChevronsUpDown },
  ]),

  /** Data Studio table ordering options shown from the toolbar sort trigger. */
  /**
   * The asset search API sorts on `name` only, so the menu offers the two
   * orders it can actually honour. "Recently added", "Recently updated" and
   * "Most used" were removed: `updated_at` and a usage count do not exist on
   * the search response at all, and sorting the fetched page by
   * `discovered_at` would silently mis-order a tenant with more assets than
   * one page. Restore them once the backend accepts those sort fields — see
   * `DATA_STUDIO_API_ALIGNMENT_PLAN.md` §7 Q1.
   */
  dataStudioSort: defineMenu([
    { id: "alphabeticalAsc", label: "Alphabetical A-Z" },
    { id: "alphabeticalDesc", label: "Alphabetical Z-A" },
  ]),

  /** Data Studio profile grid column "..." (kebab) menu. */
  dataStudioColumnMore: defineMenu([
    { id: "viewColumnDetails", label: "View column details" },
    { id: "markAsImportant", label: "Mark as important" },
  ]),

  /** AI summary kebab (three-dot) menu — insert actions (Figma reference, no icons). */
  aiSummaryInsert: defineMenu([
    { id: "insertNarrative", label: "Insert AI narrative" },
    { id: "insertRecommendation", label: "Insert AI recommendation" },
    { id: "insertInsights", label: "Insert AI insights" },
  ]),

  // ── Per-kind edit dropdowns ────────────────────────────────────────────────
  // Same actions on every kind; only the improve label names the section kind.
  // The `improveSummary` id is the shared "improve" contract consumers switch
  // on (useAiSummaryEditState, ChartWidget) — labels vary, ids never do.

  /** Narrative section edit dropdown. */
  aiNarrativeEdit: defineMenu([
    { id: "editManually", label: "Edit manually", icon: Pen },
    { id: "improveSummary", label: "Improve narrative", icon: AiSparkleIcon },
  ]),

  /** Recommendation section edit dropdown. */
  aiRecommendationEdit: defineMenu([
    { id: "editManually", label: "Edit manually", icon: Pen },
    {
      id: "improveSummary",
      label: "Improve recommendation",
      icon: AiSparkleIcon,
    },
  ]),

  /** Canvas Layout tool: arrangement presets for every tile. */
  layoutPresets: defineMenu<LayoutPresetId>(
    LAYOUT_PRESETS.map(({ id, label, description }) => ({
      id,
      label,
      description,
      icon: LAYOUT_PRESET_ICONS[id],
    })),
  ),

  /** Canvas Layout tool, new path: the Story's own layouts, which move items and write text for new places. */
  storyLayouts: defineMenu<PlanLayout>([
    { id: "dashboard_view", label: "Dashboard view", icon: LayoutDashboard },
    { id: "side_by_side_analysis", label: "Side by side analysis", icon: LayoutPanelLeft },
    { id: "briefing_doc", label: "Briefing doc", icon: FileText },
    { id: "section_grouped", label: "Section grouped", icon: LayoutList },
    { id: "editorial_spread", label: "Editorial spread", icon: Newspaper },
  ]),

  /** Insights section edit dropdown. */
  aiInsightsEdit: defineMenu([
    { id: "editManually", label: "Edit manually", icon: Pen },
    { id: "improveSummary", label: "Improve insights", icon: AiSparkleIcon },
  ]),
} as const;

export type MenuName = keyof typeof menus;

/** Per-section control config for a story-level AI text card. */
export interface SectionMenuConfig {
  /** Edit-icon dropdown — shown on every kind. */
  edit: MenuConfig;
  /** Three-dot (kebab) insert menu — present ONLY on kinds that own it. */
  more?: MenuConfig;
}

/**
 * Section kind → its controls. Single source of truth for both dropdowns:
 *  - `edit` drives the edit-icon dropdown (all kinds).
 *  - `more` drives the three-dot kebab, and its PRESENCE is the "has kebab"
 *    flag. Only `summary` carries it, so summary is the only kind that renders
 *    the kebab — no per-kind conditionals anywhere else.
 *
 * To add/retarget a dropdown, edit this map only.
 */
export const sectionMenus: Record<InsightSectionKind, SectionMenuConfig> = {
  summary: { edit: menus.aiSummaryEdit, more: menus.aiSummaryInsert },
  narrative: { edit: menus.aiNarrativeEdit },
  recommendation: { edit: menus.aiRecommendationEdit },
  insights: { edit: menus.aiInsightsEdit },
};
